import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Footprints, Timer, Droplets, Star, Zap, Trophy, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TRACKERS = [
  { key: 'steps', icon: Footprints, increments: [500, 1000, 2500], unitKey: 'stepsUnit', labelKey: 'dailySteps', testid: 'challenge-steps' },
  { key: 'active_minutes', icon: Timer, increments: [10, 20, 30], unitKey: 'minUnit', labelKey: 'activeMinutes', testid: 'challenge-minutes' },
  { key: 'water_glasses', icon: Droplets, increments: [1, 2], unitKey: 'glassesUnit', labelKey: 'waterIntake', testid: 'challenge-water' },
];

export default function ChallengesPage() {
  const { t } = useApp();
  const [data, setData] = useState(null);

  const load = () => api.get('/challenges/today').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const log = async (type, value) => {
    try {
      const res = await api.post('/challenges/log', { type, value });
      if (res.data.rewards?.completed) {
        toast.success(`${t('goalComplete')} +${res.data.rewards.xp} XP`);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="py-16 text-center text-muted-foreground text-sm">{t('loading')}</div>;

  const stats = [
    { icon: Zap, value: data.xp, label: t('xp'), testid: 'challenge-xp-card' },
    { icon: Star, value: data.stars, label: t('stars'), testid: 'challenge-stars-card' },
    { icon: Trophy, value: data.streak_days, label: t('streak'), testid: 'challenge-streak-card' },
  ];

  return (
    <div className="w-full max-w-2xl mx-auto px-3 sm:px-4 py-4 pb-24 lg:pb-8" data-testid="challenges-page">
      <div className="flex items-center gap-2 mb-5">
        <Trophy size={20} className="text-foreground" />
        <h1 className="text-xl font-semibold">{t('challenges')}</h1>
      </div>

      {/* XP / stars / streak — fluid responsive grid */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5" data-testid="challenge-xp-stars">
        {stats.map(({ icon: Icon, value, label, testid }) => (
          <div key={testid} data-testid={testid} className="min-w-0 bg-card rounded-xl border border-border p-3 sm:p-4 text-center">
            <Icon size={18} className="mx-auto text-foreground/80 mb-1" />
            <p className="text-lg sm:text-xl font-bold tabular-nums truncate">{value}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{label}</p>
          </div>
        ))}
      </div>

      {/* Trackers — fluid cards with wrapping action buttons */}
      <div className="flex flex-col gap-3 sm:gap-4" data-testid="challenge-trackers">
        {TRACKERS.map(({ key, icon: Icon, increments, unitKey, labelKey, testid }) => {
          const value = data.progress[key] || 0;
          const target = data.targets[key];
          const pct = Math.min(100, (value / target) * 100);
          const done = data.goals_completed.includes(key);
          return (
            <div key={key} className={`min-w-0 bg-card rounded-xl border p-4 sm:p-5 ${done ? 'border-foreground/40' : 'border-border'}`} data-testid={`${testid}-card`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${done ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground'}`}>
                    <Icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{t(labelKey)}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">{value.toLocaleString()} / {target.toLocaleString()} {t(unitKey)}</p>
                  </div>
                </div>
                {done && <Badge variant="secondary" className="shrink-0">+50 XP</Badge>}
              </div>
              <Progress value={pct} className="h-2 mb-3" data-testid={`${testid}-progress`} />
              <div className="flex flex-wrap gap-2">
                {increments.map((inc) => (
                  <Button key={inc} data-testid={`${testid}-add-button`} size="sm" variant="outline" onClick={() => log(key, inc)}
                    className="text-xs h-9 min-w-[44px] flex-1 sm:flex-none sm:px-4">
                    <Plus size={12} className="me-1" /> {inc.toLocaleString()} {t(unitKey)}
                  </Button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground text-center mt-5 px-2 leading-relaxed">
        {t('dailyBonus')}
      </p>
    </div>
  );
}
